import { useEffect, useRef, useState } from "react";
import { Plus, Trash2, Upload, Loader2, RefreshCw, Save, ChevronDown, ChevronUp, DownloadCloud, Wand2, FileCode, Link2, X, Search, Rss, Power } from "lucide-react";
import { toast } from "sonner";
import adminApi, { apiError } from "@/services/adminApi";
import WebGrabSection from "./WebGrabSection";

const inputCls = "px-3 py-1.5 bg-zinc-800/80 border border-zinc-700/40 rounded-lg text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-amber-500/50";

const fmtDt = (iso) => iso ? new Date(iso).toLocaleString("az-AZ", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";

function SourceCard({ src, busy, onFetch, onToggle, onDelete, onPriority }) {
  const [prio, setPrio] = useState(src.priority ?? 10);
  useEffect(() => { setPrio(src.priority ?? 10); }, [src.priority]);
  return (
    <div className={`bg-zinc-900/60 border rounded-xl p-3 ${src.enabled ? "border-zinc-800" : "border-zinc-800/50 opacity-50"}`} data-testid={`epg-source-${src.id}`}>
      <div className="flex items-center gap-2 mb-1">
        <Rss className="w-4 h-4 text-emerald-400 flex-shrink-0" />
        <p className="font-semibold text-sm truncate flex-1">{src.name}</p>
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400 uppercase">{src.type === "raspberrytv" ? "HTML" : src.type === "webgrab" ? "WG++" : "XMLTV"}</span>
        {src.lang && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-400 uppercase">{src.lang}</span>}
      </div>
      <p className="text-[10px] text-zinc-500 truncate mb-2" title={src.url}>{src.url}</p>
      <div className="flex items-center justify-between text-[11px] text-zinc-400">
        <span>{src.channel_count || 0} kanal · {src.program_count || 0} proqram</span>
        <span>Son: {fmtDt(src.last_fetched)}</span>
      </div>
      {src.last_error && <p className="text-[10px] text-red-400 truncate mt-1" title={src.last_error}>{src.last_error}</p>}
      <div className="flex items-center gap-1.5 mt-2">
        <button onClick={() => onFetch(src)} disabled={busy || !src.enabled} className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-400 rounded-lg text-xs transition-colors disabled:opacity-40" data-testid={`epg-source-fetch-${src.id}`}>
          <RefreshCw className="w-3.5 h-3.5" /> Yüklə
        </button>
        <label className="flex items-center gap-1 text-[10px] text-zinc-500" title="Prioritet (az rəqəm = üstün mənbə)">
          P:
          <input type="number" min="0" max="99" value={prio}
            onChange={(e) => setPrio(e.target.value)}
            onBlur={() => Number(prio) !== src.priority && onPriority(src, Number(prio))}
            onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
            className="w-12 px-1.5 py-1 bg-zinc-800/80 border border-zinc-700/40 rounded-lg text-xs text-white focus:outline-none focus:border-amber-500/50"
            data-testid={`epg-source-priority-${src.id}`} />
        </label>
        <button onClick={() => onToggle(src)} className={`p-1.5 rounded-lg transition-colors ${src.enabled ? "bg-zinc-800 hover:bg-zinc-700 text-zinc-300" : "bg-zinc-800/50 text-zinc-600"}`} title={src.enabled ? "Deaktiv et" : "Aktiv et"} data-testid={`epg-source-toggle-${src.id}`}>
          <Power className="w-3.5 h-3.5" />
        </button>
        <button onClick={() => onDelete(src)} className="p-1.5 text-zinc-500 hover:text-red-400 transition-colors" title="Sil" data-testid={`epg-source-delete-${src.id}`}>
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

function AddSourceForm({ onAdded }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", url: "", lang: "", priority: 10 });
  const submit = async () => {
    if (!form.name.trim() || !form.url.trim()) return toast.error("Ad və URL vacibdir");
    try {
      await adminApi.createEpgSource({ ...form, type: "xmltv" });
      toast.success("Mənbə əlavə olundu");
      setForm({ name: "", url: "", lang: "", priority: 10 });
      setOpen(false);
      onAdded();
    } catch (err) { toast.error(apiError(err)); }
  };
  if (!open) return (
    <button onClick={() => setOpen(true)} className="flex items-center justify-center gap-2 border border-dashed border-zinc-700 hover:border-zinc-500 rounded-xl p-3 text-sm text-zinc-400 hover:text-zinc-200 transition-colors min-h-[120px]" data-testid="epg-add-source-btn">
      <Plus className="w-4 h-4" /> XMLTV mənbəsi əlavə et
    </button>
  );
  return (
    <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-3 space-y-2" data-testid="epg-add-source-form">
      <input placeholder="Ad (məs: EPGShare RU)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={`${inputCls} w-full`} data-testid="epg-source-name-input" />
      <input placeholder="XMLTV URL (.xml və ya .xml.gz)" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} className={`${inputCls} w-full`} data-testid="epg-source-url-input" />
      <div className="flex gap-2">
        <input placeholder="Dil (ru/tr/az)" value={form.lang} onChange={(e) => setForm({ ...form, lang: e.target.value })} className={`${inputCls} w-24`} />
        <input type="number" placeholder="Prioritet" value={form.priority} onChange={(e) => setForm({ ...form, priority: Number(e.target.value) })} className={`${inputCls} w-24`} title="Prioritet (az = üstün)" />
        <button onClick={submit} className="flex-1 px-3 py-1.5 bg-amber-500 hover:bg-amber-400 text-black font-semibold rounded-lg text-sm transition-colors" data-testid="epg-source-save-btn">Əlavə et</button>
        <button onClick={() => setOpen(false)} className="p-2 text-zinc-500 hover:text-zinc-300"><X className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

function MappingPicker({ ch, onMapped }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await adminApi.searchFeedChannels(q);
        setResults(data.channels);
      } catch { setResults([]); }
      setLoading(false);
    }, 300);
    return () => clearTimeout(t);
  }, [q, open]);

  useEffect(() => {
    const close = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const select = async (fc) => {
    setOpen(false);
    try {
      const { data } = await adminApi.setChannelMapping(ch.id, { source_id: fc.source_id, feed_id: fc.feed_id });
      toast.success(`Uyğunlaşdırıldı: ${data.feed_name} (${data.count} proqram)`);
      onMapped();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="relative" ref={boxRef}>
      <button onClick={() => { setOpen(!open); setQ(ch.name); }} className="p-2 bg-blue-500/15 hover:bg-blue-500/25 text-blue-400 rounded-lg transition-colors" title="Manual uyğunlaşdır" data-testid={`epg-map-btn-${ch.id}`}>
        <Link2 className="w-4 h-4" />
      </button>
      {open && (
        <div className="absolute right-0 top-10 z-30 w-80 bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl p-2" data-testid={`epg-map-dropdown-${ch.id}`}>
          <div className="relative mb-2">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-zinc-500" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Feed kanalını axtar..." className={`${inputCls} w-full pl-8`} data-testid={`epg-map-search-${ch.id}`} />
          </div>
          <div className="max-h-56 overflow-y-auto">
            {loading && <p className="text-xs text-zinc-500 p-2 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Axtarılır...</p>}
            {!loading && results.length === 0 && <p className="text-xs text-zinc-500 p-2">Nəticə yoxdur</p>}
            {!loading && results.map((fc) => (
              <button key={`${fc.source_id}::${fc.feed_id}`} onClick={() => select(fc)} className="w-full text-left px-2 py-1.5 hover:bg-zinc-800 rounded-lg transition-colors">
                <p className="text-xs text-zinc-200 truncate">{fc.display_name}</p>
                <p className="text-[10px] text-zinc-500 truncate">{fc.source_name}</p>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ChannelRow({ ch, sourceNames, onReload }) {
  const [expanded, setExpanded] = useState(false);
  const [programs, setPrograms] = useState(null);
  const [siteUrl, setSiteUrl] = useState(ch.site_url || "");
  const [saving, setSaving] = useState(false);
  const [parsing, setParsing] = useState(false);

  const toggleExpand = async () => {
    if (!expanded && programs === null) {
      const { data } = await adminApi.channelPrograms(ch.id);
      setPrograms(data.programs);
    }
    setExpanded(!expanded);
  };

  const handleUnmap = async () => {
    try {
      await adminApi.clearChannelMapping(ch.id);
      toast.success("Uyğunlaşdırma silindi");
      onReload();
    } catch (err) { toast.error(apiError(err)); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`"${ch.name}" silinsin?`)) return;
    await adminApi.deleteEpgChannel(ch.id);
    onReload();
  };

  const handleSaveUrl = async () => {
    setSaving(true);
    try {
      await adminApi.updateEpgChannel(ch.id, { site_url: siteUrl });
      toast.success("Yadda saxlandı");
      onReload();
    } catch (err) { toast.error(apiError(err)); }
    setSaving(false);
  };

  const handleParse = async () => {
    setParsing(true);
    try {
      const { data } = await adminApi.parseChannel(ch.id);
      toast.success(`${data.count} proqram tapıldı`);
      setPrograms(null);
      onReload();
    } catch (err) { toast.error(apiError(err)); }
    setParsing(false);
  };

  const fmt = (ts) => new Date(ts * 1000).toLocaleString("az-AZ", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  const mapped = !!ch.map_feed_id;
  const htmlSource = !mapped && ch.site_url;

  return (
    <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-3" data-testid={`epg-channel-${ch.id}`}>
      <div className="flex items-center gap-2">
        <div className="w-44 flex-shrink-0">
          <p className="font-semibold text-sm truncate">{ch.name}</p>
          <p className="text-[10px] text-zinc-500">
            {ch.program_count > 0 ? `${ch.program_count} proqram` : "Proqram yoxdur"}
            {ch.last_parsed && ` · ${fmtDt(ch.last_parsed)}`}
          </p>
        </div>
        <div className="flex-1 min-w-0">
          {mapped && (
            <span className="inline-flex items-center gap-1.5 max-w-full px-2 py-1 rounded-lg bg-emerald-500/10 border border-emerald-500/20" data-testid={`epg-mapping-badge-${ch.id}`}>
              <span className="text-xs text-emerald-400 truncate">{ch.map_feed_name}</span>
              <span className="text-[10px] text-zinc-500 flex-shrink-0">{sourceNames[ch.map_source_id] || ""} · {ch.map_type === "manual" ? "manual" : "avto"}</span>
              <button onClick={handleUnmap} className="text-zinc-500 hover:text-red-400 flex-shrink-0" title="Uyğunlaşdırmanı sil" data-testid={`epg-unmap-${ch.id}`}><X className="w-3 h-3" /></button>
            </span>
          )}
          {htmlSource && (
            <span className="inline-flex items-center gap-1.5 max-w-full px-2 py-1 rounded-lg bg-violet-500/10 border border-violet-500/20">
              <span className="text-xs text-violet-400 truncate" title={ch.site_url}>HTML: {ch.site_url.replace("https://", "")}</span>
            </span>
          )}
          {!mapped && !htmlSource && <span className="text-xs text-zinc-600">Uyğunlaşdırılmayıb</span>}
          {ch.last_error && <p className="text-[10px] text-red-400 truncate mt-0.5" title={ch.last_error}>{ch.last_error}</p>}
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <MappingPicker ch={ch} onMapped={onReload} />
          <button onClick={toggleExpand} className="p-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg transition-colors" title="Ətraflı" data-testid={`epg-expand-${ch.id}`}>
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          <button onClick={handleDelete} className="p-2 text-zinc-400 hover:text-red-400 transition-colors" title="Sil">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      {expanded && (
        <div className="mt-3 border-t border-zinc-800 pt-3 space-y-3">
          <div className="flex gap-2 items-center">
            <input placeholder="HTML sayt URL-i (raspberrytv və s.)" value={siteUrl} onChange={(e) => setSiteUrl(e.target.value)} className={`${inputCls} flex-1 min-w-0`} data-testid={`epg-siteurl-${ch.id}`} />
            <button onClick={handleSaveUrl} disabled={saving} className="p-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg transition-colors" title="Yadda saxla">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            </button>
            <button onClick={handleParse} disabled={parsing || !siteUrl} className="p-2 bg-amber-500/15 hover:bg-amber-500/25 text-amber-400 rounded-lg transition-colors disabled:opacity-40" title="HTML parse et" data-testid={`epg-parse-${ch.id}`}>
              {parsing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </button>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {(programs || []).length === 0 && <p className="text-xs text-zinc-500 py-1">Proqram yoxdur</p>}
            {(programs || []).map((p, i) => (
              <div key={i} className="flex gap-3 text-xs py-1 text-zinc-400">
                <span className="font-mono text-zinc-500 flex-shrink-0">{fmt(p.start_ts)}</span>
                <span className="truncate">{p.title}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function EpgAdminPage() {
  const [channels, setChannels] = useState([]);
  const [sources, setSources] = useState([]);
  const [hubState, setHubState] = useState({ phase: "idle" });
  const [newName, setNewName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = () => {
    adminApi.epgChannels().then(({ data }) => setChannels(data.channels)).catch(() => {});
    adminApi.epgSources().then(({ data }) => { setSources(data.sources); setHubState(data.state); }).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const busy = hubState.phase !== "idle";

  useEffect(() => {
    if (!busy) return;
    const t = setInterval(async () => {
      try {
        const { data } = await adminApi.epgHubStatus();
        setHubState(data);
        if (data.phase === "idle") {
          clearInterval(t);
          toast.success("Hub əməliyyatı tamamlandı");
          load();
        }
      } catch { /* keep polling */ }
    }, 3000);
    return () => clearInterval(t);
  }, [busy]);

  const startTask = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      setHubState({ phase: "fetching", done: 0, total: 1, detail: "" });
    } catch (err) { toast.error(apiError(err)); }
  };

  const handleToggleSource = async (src) => {
    try {
      await adminApi.updateEpgSource(src.id, { enabled: !src.enabled });
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const handlePriority = async (src, priority) => {
    try {
      await adminApi.updateEpgSource(src.id, { priority });
      toast.success(`${src.name}: prioritet ${priority} oldu`);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const handleDeleteSource = async (src) => {
    if (!window.confirm(`"${src.name}" mənbəsi silinsin? Bu mənbəyə bağlı uyğunlaşdırmalar sıfırlanacaq.`)) return;
    try {
      await adminApi.deleteEpgSource(src.id);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const handleImportProvider = async () => {
    setImporting(true);
    try {
      const { data } = await adminApi.importProviderChannels();
      toast.success(`${data.added} kanal import olundu, ${data.skipped} mövcud idi`);
      load();
    } catch (err) { toast.error(apiError(err)); }
    setImporting(false);
  };

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const { data } = await adminApi.uploadChannelList(file);
      toast.success(`${data.added} kanal əlavə olundu, ${data.skipped} mövcud idi`);
      load();
    } catch (err) { toast.error(apiError(err)); }
    setUploading(false);
  };

  const handleAdd = async () => {
    if (!newName.trim()) return;
    try {
      await adminApi.createEpgChannel({ name: newName.trim() });
      setNewName("");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const sourceNames = Object.fromEntries(sources.map((s) => [s.id, s.name]));
  const withEpg = channels.filter((c) => c.program_count > 0).length;
  const mappedCount = channels.filter((c) => c.map_feed_id).length;
  const filtered = channels.filter((c) => {
    if (filter === "mapped") return !!c.map_feed_id;
    if (filter === "unmapped") return !c.map_feed_id && !(c.site_url && c.program_count > 0);
    if (filter === "noepg") return !c.program_count;
    return true;
  });

  return (
    <div data-testid="admin-epg-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-bold">EPG Server — XMLTV Hub</h1>
          <p className="text-sm text-zinc-500 mt-1">Mənbələr hər gecə 03:00-da avtomatik yenilənir və kanallar ağıllı uyğunlaşdırılır</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={handleImportProvider} disabled={importing} className="flex items-center gap-2 px-4 py-2 bg-blue-600/15 hover:bg-blue-600/25 text-blue-400 border border-blue-500/30 rounded-lg text-sm transition-colors disabled:opacity-50" data-testid="epg-import-provider-btn">
            {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <DownloadCloud className="w-4 h-4" />} Provayderdən import
          </button>
          <label className="flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-sm cursor-pointer transition-colors" data-testid="epg-upload-label">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Siyahı yüklə
            <input type="file" accept=".m3u,.m3u8,.xlsx,.csv,.txt" className="hidden" onChange={(e) => handleUpload(e.target.files[0])} />
          </label>
          <button onClick={() => startTask(adminApi.fetchAllEpgSources, "Bütün mənbələr yüklənir...")} disabled={busy} className="flex items-center gap-2 px-4 py-2 bg-emerald-600/15 hover:bg-emerald-600/25 text-emerald-400 border border-emerald-500/30 rounded-lg text-sm transition-colors disabled:opacity-50" data-testid="epg-fetch-all-btn">
            <RefreshCw className="w-4 h-4" /> Mənbələri yenilə
          </button>
          <button onClick={() => startTask(() => adminApi.autoMapEpg(false), "Ağıllı uyğunlaşdırma başladı...")} disabled={busy} className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black font-semibold rounded-lg text-sm transition-colors" data-testid="epg-automap-btn">
            <Wand2 className="w-4 h-4" /> Avto-uyğunlaşdır
          </button>
        </div>
      </div>

      {busy && (
        <div className="flex items-center gap-3 bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 mb-5" data-testid="epg-hub-progress">
          <Loader2 className="w-4 h-4 animate-spin text-amber-400 flex-shrink-0" />
          <p className="text-sm text-amber-300">
            {hubState.phase === "fetching" ? "Mənbələr yüklənir" : "Uyğunlaşdırılır"}: {hubState.done}/{hubState.total}
            {hubState.detail && <span className="text-amber-400/70"> — {hubState.detail}</span>}
            {hubState.phase === "mapping" && ` · ${hubState.mapped} tapıldı`}
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 mb-5" data-testid="epg-sources-grid">
        {sources.map((src) => (
          <SourceCard key={src.id} src={src} busy={busy}
            onFetch={(s) => startTask(() => adminApi.fetchEpgSource(s.id), `${s.name} yüklənir...`)}
            onToggle={handleToggleSource} onDelete={handleDeleteSource} onPriority={handlePriority} />
        ))}
        <AddSourceForm onAdded={load} />
      </div>

      <WebGrabSection busy={busy} onImportStarted={() => setHubState({ phase: "fetching", done: 0, total: 1, detail: "WebGrab guide.xml" })} />

      <div className="flex items-start gap-3 bg-zinc-900/60 border border-zinc-800 rounded-xl p-4 mb-5" data-testid="xmltv-info">
        <FileCode className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-zinc-400">
          <p className="font-semibold text-zinc-200 mb-1">XMLTV Export (middleware üçün)</p>
          <p>
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/epg-server/xmltv`} target="_blank" rel="noreferrer" className="text-emerald-400 hover:underline font-mono text-xs">/api/epg-server/xmltv</a>
            {" · "}
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/epg-server/xmltv.gz`} className="text-emerald-400 hover:underline font-mono text-xs">/api/epg-server/xmltv.gz</a>
          </p>
        </div>
      </div>

      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4">
        <div className="flex gap-2">
          <input
            data-testid="epg-new-channel-input"
            placeholder="Yeni kanal adı"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAdd()}
            className={`${inputCls} w-56`}
          />
          <button data-testid="epg-add-channel-btn" onClick={handleAdd} className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-sm transition-colors">
            <Plus className="w-4 h-4" /> Əlavə et
          </button>
        </div>
        <div className="flex items-center gap-2 md:ml-auto">
          <span className="text-xs text-zinc-500" data-testid="epg-stats">{withEpg}/{channels.length} kanal EPG-li · {mappedCount} XMLTV map</span>
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className={`${inputCls}`} data-testid="epg-filter-select">
            <option value="all">Hamısı</option>
            <option value="mapped">XMLTV map olunmuş</option>
            <option value="unmapped">Map olunmamış</option>
            <option value="noepg">EPG-siz</option>
          </select>
        </div>
      </div>

      <div className="space-y-2">
        {filtered.length === 0 && <p className="text-zinc-500 text-sm">Kanal yoxdur.</p>}
        {filtered.map((ch) => (
          <ChannelRow key={ch.id} ch={ch} sourceNames={sourceNames} onReload={load} />
        ))}
      </div>
    </div>
  );
}
